/**
 * Compact Slot Machine Trigger Button
 *
 * A simple button for the hero section that shows spin availability
 * and triggers the full 3D slot machine modal when clicked.
 */

import { Loader2, Gift } from 'lucide-react';
import { useLanguage } from '../../../../contexts/LanguageContext';
import { useAuthStore } from '../../../../stores/auth-store';
import { useSpinStatus, useNextSpinCountdown } from '../hooks/useDailySpin';

interface Props {
  onSpin: () => void;
  onLoginRequired: () => void;
}

export function SlotMachine3DCompact({ onSpin, onLoginRequired }: Props) {
  const { t } = useLanguage();
  const { user } = useAuthStore();
  const { data: spinStatus, isLoading } = useSpinStatus();
  const countdown = useNextSpinCountdown(spinStatus?.canSpin ?? false);

  const canSpin = spinStatus?.canSpin;

  const handleClick = () => {
    if (!user) {
      onLoginRequired();
      return;
    }
    onSpin();
  };

  if (isLoading) {
    return (
      <div className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-action-light to-action-dark rounded-xl">
        <Loader2 className="w-4 h-4 animate-spin text-white" />
      </div>
    );
  }

  return (
    <button
      onClick={handleClick}
      className={`flex items-center gap-2 px-4 py-2 rounded-xl font-bold text-sm transition-all ${
        canSpin
          ? 'bg-gradient-to-r from-action-light to-action-dark text-white hover:from-action-dark hover:to-action-dark shadow-lg shadow-action-dark/20 hover:shadow-action-dark/40'
          : 'bg-gray-200 text-gray-500'
      }`}
    >
      <Gift className="w-4 h-4" />
      {canSpin ? (
        t('slotMachine.spinNow', 'Spin Now!')
      ) : countdown ? (
        countdown
      ) : (
        t('slotMachine.spunToday', 'Spun Today')
      )}
    </button>
  );
}

export default SlotMachine3DCompact;
